'use client'

import { usePathname } from 'next/navigation'
import React, {
  Dispatch,
  useEffect,
  createContext,
  useContext,
  ReactNode,
  useReducer,
  Reducer,
} from 'react'

/* --- Common type defs --- */
export enum SubMenu {
  PRODUCTS = 'PRODUCTS',
  SOLUTIONS = 'SOLUTIONS',
}

/* --- Header state --- */
const minicartStorageKey = 'dustin.header.showMinicart'

export enum HeaderStateActionType {
  SHOW_MINICART_ONLOAD = 'SHOW_MINICART',
  HIDE_MINICART_ONLOAD = 'HIDE_MINICART',
}

interface HeaderStateAction {
  type: HeaderStateActionType
}

type HeaderState = {
  showMinicartOnLoad?: boolean
}

/**
 * Reducer for header state
 */
const headerReducer: Reducer<HeaderState, HeaderStateAction> = (prevState, action) => {
  switch (action.type) {
    case HeaderStateActionType.SHOW_MINICART_ONLOAD:
      localStorage.setItem(minicartStorageKey, 'true')
      return { ...prevState, showMinicartOnLoad: true }
    case HeaderStateActionType.HIDE_MINICART_ONLOAD:
      localStorage.removeItem(minicartStorageKey)
      return { ...prevState, showMinicartOnLoad: false }
  }
}

/* --- Mobile state --- */
export enum MobileStateActionType {
  SHOW_MENU = 'SHOW_MENU',
  HIDE_MENU = 'HIDE_MENU',
  SHOW_SEARCH = 'SHOW_SEARCH',
  HIDE_SEARCH = 'HIDE_SEARCH',
  SHOW_ACCOUNT = 'SHOW_ACCOUNT',
  HIDE_ACCOUNT = 'HIDE_ACCOUNT',
  SET_ACTIVE_SUB_MENU = 'SET_ACTIVE_SUB_MENU',
  SET_BREADCRUMBS = 'SET_BREADCRUMBS',
  RESET_STATE = 'RESET_STATE',
}

type MobileBreadcrumb = {
  title?: string
  categoryId?: string
  action: () => void
}
interface MobileStateAction {
  type: MobileStateActionType
  payload?: {
    subMenu?: SubMenu | null
    breadcrumbs?: MobileBreadcrumb[] | null
  }
}

type MobileState = {
  showMenu: boolean
  showSearch: boolean
  showMyAccount: boolean
  isSolutionsOpen: boolean
  breadcrumbs: MobileBreadcrumb[] | null
}

/**
 * Reducer for mobile state
 */
const mobileReducer: Reducer<MobileState, MobileStateAction> = (prevState, action) => {
  const isSolutionsOpen = action.payload?.subMenu === SubMenu.SOLUTIONS

  switch (action.type) {
    case MobileStateActionType.SHOW_MENU:
      return { ...prevState, showMenu: true }
    case MobileStateActionType.HIDE_MENU:
      return { ...prevState, showMenu: false }
    case MobileStateActionType.SHOW_SEARCH:
      return { ...prevState, showSearch: true }
    case MobileStateActionType.HIDE_SEARCH:
      return { ...prevState, showSearch: false }
    case MobileStateActionType.SHOW_ACCOUNT:
      return { ...prevState, showMyAccount: true }
    case MobileStateActionType.HIDE_ACCOUNT:
      return { ...prevState, showMyAccount: false }
    case MobileStateActionType.SET_ACTIVE_SUB_MENU:
      return {
        ...prevState,
        isSolutionsOpen,
      }
    case MobileStateActionType.SET_BREADCRUMBS:
      return {
        ...prevState,
        breadcrumbs: action.payload?.breadcrumbs ?? null,
      }
    case MobileStateActionType.RESET_STATE:
      return { ...prevState, ...action.payload }
  }
}

/* --- Desktop state type defs --- */
export enum DesktopStateActionType {
  SET_MENU_ACTIVE = 'SET_MENU_ACTIVE',
  SET_ACTIVE_MEGA_MENU = 'SET_ACTIVE_MEGA_MENU',
  SET_FOCUS_MEGA_MENU = 'SET_FOCUS_MEGA_MENU',
  CLOSE_ALL_MENUS = 'CLOSE_ALL_MENUS',
  RESET_STATE = 'RESET_STATE',
}

export type DesktopState = {
  activeMegaMenu: string
  focusMegaMenu: string
  activeMenus: string[]
}

export interface DesktopStateAction {
  type: DesktopStateActionType
  payload?: {
    activeMegaMenu?: string
    focusMegaMenu?: string
    menu?: { name: string; active: boolean }
  }
}

/**
 * Reducer for desktop state
 */
const desktopReducer: Reducer<DesktopState, DesktopStateAction> = (prevState, action) => {
  let activeMenus = [...prevState.activeMenus]
  if (action.payload?.menu?.active) {
    activeMenus.push(action.payload.menu.name)
  } else activeMenus = activeMenus.filter(name => name !== action.payload?.menu?.name)

  switch (action.type) {
    case DesktopStateActionType.SET_ACTIVE_MEGA_MENU:
      return { ...prevState, activeMegaMenu: action.payload?.activeMegaMenu ?? '' }
    case DesktopStateActionType.SET_FOCUS_MEGA_MENU:
      return { ...prevState, focusMegaMenu: action.payload?.focusMegaMenu ?? '' }
    case DesktopStateActionType.SET_MENU_ACTIVE:
      return { ...prevState, activeMenus }
    case DesktopStateActionType.CLOSE_ALL_MENUS:
      return {
        ...prevState,
        activeMegaMenu: '',
        activeMenus: [],
      }

    case DesktopStateActionType.RESET_STATE:
      return { ...prevState, ...action.payload }

    default:
      return prevState
  }
}

/**
 * Header context type definition
 */
type HeaderContextType = {
  header: HeaderState
  dispatchHeader: Dispatch<HeaderStateAction>
  mobile: MobileState
  dispatchMobile: Dispatch<MobileStateAction>
  desktop: DesktopState
  dispatchDesktop: Dispatch<DesktopStateAction>
}

/**
 * Initial state for HeaderContext
 */
const initialState: HeaderContextType = {
  header: {},
  dispatchHeader: () => null,
  mobile: {
    showMenu: false,
    showSearch: false,
    showMyAccount: false,
    isSolutionsOpen: false,
    breadcrumbs: null,
  },
  dispatchMobile: () => null,
  desktop: {
    activeMegaMenu: '',
    focusMegaMenu: '',
    activeMenus: [],
  },
  dispatchDesktop: () => null,
}

/**
 * HeaderContext definition
 */
export const HeaderContext = createContext<HeaderContextType>(initialState)

/**
 * Hook that returns the current HeaderContext
 */
export const useHeaderContext = () => {
  return useContext(HeaderContext)
}

/**
 * Context provider that wraps the Header component
 */
export const HeaderContextProvider = ({ children }: { children: ReactNode }) => {
  const url = usePathname()

  const getDefaultHeaderState = () => ({})

  const getDefaultMobileState = () => ({
    showMenu: false,
    showSearch: false,
    showMyAccount: false,
    isSolutionsOpen: false,
    breadcrumbs: null,
  })

  const getDefaultDesktopState = () => ({
    activeMegaMenu: '',
    focusMegaMenu: '',
    activeMenus: [],
  })

  const [header, dispatchHeader] = useReducer(headerReducer, getDefaultHeaderState())
  const [mobile, dispatchMobile] = useReducer(mobileReducer, getDefaultMobileState())
  const [desktop, dispatchDesktop] = useReducer(desktopReducer, getDefaultDesktopState())

  useEffect(() => {
    dispatchDesktop({ type: DesktopStateActionType.RESET_STATE, payload: getDefaultDesktopState() })
    dispatchMobile({ type: MobileStateActionType.RESET_STATE, payload: getDefaultMobileState() })
  }, [url])

  useEffect(() => {
    if (localStorage.getItem(minicartStorageKey) === 'true') {
      dispatchHeader({
        type: HeaderStateActionType.SHOW_MINICART_ONLOAD,
      })
    }
  }, [])

  const state = {
    header: header,
    dispatchHeader,
    mobile,
    dispatchMobile,
    desktop,
    dispatchDesktop,
  }

  return <HeaderContext.Provider value={state}>{children}</HeaderContext.Provider>
}
